import React, { useEffect, useMemo, useState } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import DocumentSearchFilters, { DocumentSearchStatusOptionType } from './DocumentSearchFilters';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useDispatch, useSelector } from 'react-redux';
import NavAppbar from '../nav-top/NavAppbar';
import PageTitle from '../page-title/PageTitle';
import {
  searchBidFiles,
  searchDesignFiles,
  searchDocuments,
  searchEmails,
} from '../../models/api/search';
import {
  ActionTakenType,
  DocumentTemplateType,
  PunchListStatusType,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import _ from 'lodash';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { useAppUtilsContext } from '../AppUtilsContext';
import { Dayjs } from 'dayjs';
import { AddCircleOutlined } from '@material-ui/icons';
import IconButton from '@material-ui/core/IconButton';
import { isDefined, useQuery } from '../../scripts/utils';
import { useLocation } from 'react-router';
import FullscreenLoader from '../loader/FullscreenLoader';
import EllipsisLoader from '../loader/EllipsisLoader';
import ProjectSearchResults, { IProjectSearchResult } from './ProjectSearchResults';
import getProjectsState from '../../features/projects/selectors';
import { getFavoriteProjects } from '../../models/api/project';
import { setProject } from '../../features/project/actions';
import { hasSearchPermissions } from '../../scripts/store-utils';
import Grid from '@material-ui/core/Grid';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    paper: {
      height: '100%',
      minHeight: '100vh',
      maxWidth: 800,
    },
    content: {
      flexGrow: 1,
      padding: theme.spacing(3),
      minHeight: '100vh',
      overflowX: 'hidden',
      overflowY: 'hidden',
    },
    searchForm: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
      width: '100%',
    },
    searchInput: {
      marginRight: theme.spacing(2),
      width: '100%',
    },
    resultsContainer: {
      marginTop: theme.spacing(2),
    },
    noResults: {
      padding: theme.spacing(2),
      textAlign: 'center',
    },
  }),
);

interface ISearchState {
  selectedProjectIds: string[];
  documentTypes: (DocumentTemplateType | string)[];
  includeEmails: boolean;
  statuses: DocumentSearchStatusOptionType[];
  createdAfterDate: Dayjs | null;
  createdBeforeDate: Dayjs | null;
  creatorUserIds: string[];
  assignedToUserIds: string[];
}

const getEmptySearchState = (projectId?: string | null): ISearchState => ({
  selectedProjectIds: projectId ? [projectId] : [],
  documentTypes: [],
  includeEmails: false,
  statuses: [],
  createdAfterDate: null,
  createdBeforeDate: null,
  creatorUserIds: [],
  assignedToUserIds: [],
});

export default function SearchPage() {
  const classes = useStyles();

  const dispatch = useDispatch();
  const projects = useSelector(getProjectsState);
  const params = useQuery();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation<{ fileManagerFilter?: boolean }>();

  const {
    documentTemplates: { documentTemplateNameToId },
  } = useAppUtilsContext();

  const defaultSearchInput = params.get('query') || '';
  const defaultProjectId = params.get('projectId');
  const fileManagerFilter = Boolean(location.state?.fileManagerFilter);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);

  const [searchInput, setSearchInput] = useState(defaultSearchInput);
  const [searchQuery, setSearchQuery] = useState('');
  const [keywordSearchInputs, setKeywordSearchInputs] = useState<string[]>(['']);
  const [keywordSearchQueries, setKeywordSearchQueries] = useState<string[]>([]);

  const [projectLoading, setProjectLoading] = useState<Record<string, boolean>>({});
  const [searchResults, setSearchResults] = useState<_.Dictionary<IProjectSearchResult>>({});

  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>(
    defaultProjectId ? [defaultProjectId] : [],
  );
  const [documentTemplateIds, setDocumentTemplateIds] = useState<string[]>([]);
  const [shouldSearchEmailsFilter, setShouldSearchEmailsFilter] = useState(false);
  const [shouldSearchFileManager, setShouldSearchFileManager] = useState(false);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatusType[]>([]);
  const [punchListStatuses, setPunchListStatuses] = useState<PunchListStatusType[]>([]);
  const [actionTakenValues, setActionTakenValues] = useState<ActionTakenType[]>([]);
  const [createdAfterDate, setCreatedAfterDate] = useState<string | undefined>(undefined);
  const [createdBeforeDate, setCreatedBeforeDate] = useState<string | undefined>(undefined);
  const [creatorUserIds, setCreatorUserIds] = useState<string[]>([]);
  const [assignedToUserIds, setAssignedToUserIds] = useState<string[]>([]);

  const [documentSearchFilters, setDocumentSearchFilters] = useState<
    | {
        selectedProjectIds: string[];
        documentTypes: (DocumentTemplateType | string)[];
        includeEmails: boolean;
        statuses: DocumentSearchStatusOptionType[];
        createdAfterDate: Dayjs | null;
        createdBeforeDate: Dayjs | null;
        creatorUserIds: string[];
        assignedToUserIds: string[];
      }
    | undefined
  >(getEmptySearchState(defaultProjectId));

  const bidFileDocumentTemplateIds = useMemo(
    () =>
      [DocumentTemplateType.ProjectManual, DocumentTemplateType.BidDrawings]
        .map((name) => documentTemplateNameToId[name])
        .filter(isDefined),
    [documentTemplateNameToId],
  );

  const selectedProjects = useMemo(
    () =>
      selectedProjectIds
        .map((projectId) => projects.find((x) => x.id === projectId))
        .filter(isDefined),
    [selectedProjectIds, projects],
  );

  const allSearchResults = useMemo(() => {
    const perProject = Object.values(searchResults);

    const docs = perProject.flatMap((p) => Object.values(p.documents).flat());
    const emails = perProject.flatMap((p) => p.emails);
    const files = perProject.flatMap((p) => p.designFiles);
    const bidFiles = perProject.flatMap((p) => p.bidFiles);

    return [...docs, ...emails, ...files, ...bidFiles];
  }, [searchResults]);

  useEffect(() => {
    dispatch(setProject(null));
  }, []);

  useEffect(() => {
    setHasPermission(hasSearchPermissions(projects));
  }, [projects]);

  useEffect(() => {
    if (!defaultProjectId) {
      getFavoriteProjects()
        .then((res) => {
          const validProjectIds = res.favoriteProjects.filter((x) => x.canSearch).map((x) => x.id);
          setSelectedProjectIds(validProjectIds);
          setDocumentSearchFilters((prev) => ({
            ...(prev || getEmptySearchState()),
            selectedProjectIds: validProjectIds,
          }));
        })
        .then(() => setPageIsLoading(false));
    } else {
      setPageIsLoading(false);
    }
  }, [defaultProjectId]);

  useEffect(() => {
    if ((!defaultProjectId && !defaultSearchInput) || pageIsLoading || !hasPermission) return;

    if (fileManagerFilter) {
      setShouldSearchFileManager(true);
      setDocumentTemplateIds([]);
      setShouldSearchEmailsFilter(false);

      setDocumentSearchFilters({
        ...getEmptySearchState(defaultProjectId),
        documentTypes: ['CENTERLINE Drive'],
      });

      handleSearch({
        shouldSearchFileManager: true,
        documentTemplateIds: [],
        shouldSearchEmailsFilter: false,
      });
    } else {
      handleSearch();
    }
  }, [defaultProjectId, defaultSearchInput, fileManagerFilter, pageIsLoading, hasPermission]);

  useEffect(() => {
    if (hasSearched) {
      handleSearch();
    }
  }, [
    selectedProjects,
    documentTemplateIds,
    shouldSearchFileManager,
    workflowStatuses,
    punchListStatuses,
    actionTakenValues,
    createdAfterDate,
    createdBeforeDate,
    creatorUserIds,
    assignedToUserIds,
  ]);

  const handleOpenFilters = () => {
    setIsFiltersOpen(true);
  };
  const handleCloseFilters = () => {
    setIsFiltersOpen(false);
  };

  const handleSetFilters = (
    selectedProjectIds: string[],
    documentTemplateNames: (DocumentTemplateType | string)[],
    includeEmails: boolean,
    statuses: DocumentSearchStatusOptionType[],
    createdAfterDateParam: Dayjs | null,
    createdBeforeDateParam: Dayjs | null,
    creatorUserIdsParam: string[],
    assignedToUserIdsParam: string[],
  ) => {
    const fileManagerSelected = documentTemplateNames.includes('CENTERLINE Drive');
    setShouldSearchFileManager(fileManagerSelected);

    setSelectedProjectIds(selectedProjectIds);

    const validDocumentTemplateNames = documentTemplateNames.filter(
      (name): name is DocumentTemplateType => name in documentTemplateNameToId,
    );

    setDocumentTemplateIds(
      validDocumentTemplateNames.map((name) => documentTemplateNameToId[name]),
    );
    setShouldSearchEmailsFilter(includeEmails);

    const workflowStatusesTemp: WorkflowStatusType[] = [];
    const punchListStatusesTemp: PunchListStatusType[] = [];
    const actionTakenValuesTemp: ActionTakenType[] = [];

    statuses.forEach((status) => {
      switch (status) {
        case DocumentSearchStatusOptionType.AWAITING_SUBMISSION:
          workflowStatusesTemp.push(
            WorkflowStatusType.Initial,
            WorkflowStatusType.SubcontractorUploaded,
            WorkflowStatusType.ReadyForSubmissionToArchitect,
            WorkflowStatusType.GeneralContractorUploaded,
          );
          punchListStatusesTemp.push(PunchListStatusType.New);
          break;
        case DocumentSearchStatusOptionType.SUBMITTED_FOR_REVIEW:
          workflowStatusesTemp.push(WorkflowStatusType.SubmittedForReview);
          break;
        case DocumentSearchStatusOptionType.UNDER_REVIEW:
          workflowStatusesTemp.push(
            WorkflowStatusType.UnderReview,
            WorkflowStatusType.ArchitectUploaded,
          );
          punchListStatusesTemp.push(PunchListStatusType.InProgress);
          break;
        case DocumentSearchStatusOptionType.REVIEW_COMPLETE:
          workflowStatusesTemp.push(
            WorkflowStatusType.ReviewComplete,
            WorkflowStatusType.Completed,
          );
          punchListStatusesTemp.push(PunchListStatusType.ReadyForVerification);
          break;
        case DocumentSearchStatusOptionType.RESUBMITTED:
          workflowStatusesTemp.push(WorkflowStatusType.Resubmitted);
          punchListStatusesTemp.push(PunchListStatusType.Rejected);
          actionTakenValuesTemp.push(ActionTakenType.ReviseAndResubmit);
          break;
        case DocumentSearchStatusOptionType.RESOLVED:
          workflowStatusesTemp.push(WorkflowStatusType.Resolved, WorkflowStatusType.Accepted);
          punchListStatusesTemp.push(PunchListStatusType.Accepted);
          break;
      }
    });

    setWorkflowStatuses(workflowStatusesTemp);
    setPunchListStatuses(punchListStatusesTemp);
    setActionTakenValues(actionTakenValuesTemp);

    if (createdAfterDateParam) {
      setCreatedAfterDate(createdAfterDateParam.startOf('day').toISOString());
    }
    if (createdBeforeDateParam) {
      setCreatedBeforeDate(createdBeforeDateParam.endOf('day').toISOString());
    }

    setCreatorUserIds(creatorUserIdsParam);
    setAssignedToUserIds(assignedToUserIdsParam);

    setDocumentSearchFilters({
      selectedProjectIds,
      documentTypes: documentTemplateNames,
      includeEmails,
      statuses,
      createdAfterDate: createdAfterDateParam,
      createdBeforeDate: createdBeforeDateParam,
      creatorUserIds: creatorUserIdsParam,
      assignedToUserIds: assignedToUserIdsParam,
    });
  };

  const handleSearch = async (overrides?: {
    shouldSearchFileManager?: boolean;
    documentTemplateIds?: string[];
    shouldSearchEmailsFilter?: boolean;
  }) => {
    if (selectedProjectIds.length === 0 || !searchInput.trim()) return;

    const cleanedInput = searchInput.trim();
    const cleanedKeywordSearchInputs = keywordSearchInputs.map((input) => input.trim());

    setSearchQuery(cleanedInput);
    setKeywordSearchQueries(cleanedKeywordSearchInputs);

    setSearchResults({});
    setHasSearched(true);
    setIsSearching(true);

    setProjectLoading(
      selectedProjectIds.reduce<Record<string, boolean>>((acc, projectId) => {
        acc[projectId] = true;
        return acc;
      }, {}),
    );

    try {
      const fileManagerFlag = overrides?.shouldSearchFileManager ?? shouldSearchFileManager;
      const docTemplateIds = overrides?.documentTemplateIds ?? documentTemplateIds;
      const emailsFlag = overrides?.shouldSearchEmailsFilter ?? shouldSearchEmailsFilter;

      const hasFiltersApplied = docTemplateIds.length > 0 || emailsFlag || fileManagerFlag;

      const shouldSearchEmails = hasFiltersApplied ? emailsFlag : true;
      const shouldSearchDocuments = hasFiltersApplied ? docTemplateIds.length > 0 : true;
      const shouldSearchDesignFiles = hasFiltersApplied ? fileManagerFlag : true;
      // A bid setup's own files have no document behind them, but the contractor reads bid
      // specifications as the bid package's "Project Manual" and bid drawings as its "Plans", so
      // they answer to the document types of those names rather than to a filter of their own.
      const shouldSearchBidFiles = hasFiltersApplied
        ? bidFileDocumentTemplateIds.some((id) => docTemplateIds.includes(id))
        : true;

      await Promise.all(
        selectedProjects.map(async (p) => {
          try {
            const [
              documentResults,
              emailResults,
              designFilesResults,
              bidFilesResults,
            ] = await Promise.all([
              shouldSearchDocuments
                ? searchDocuments(p.id, {
                    searchTerm: cleanedInput,
                    keywordSearchTerms: cleanedKeywordSearchInputs,
                    documentTemplateIds: docTemplateIds,
                    workflowStatuses,
                    punchListStatuses,
                    actionTakenValues,
                    createdAfterDate,
                    createdBeforeDate,
                    creatorUserIds,
                    assignedToUserIds,
                  })
                : Promise.resolve({ results: [] }),
              shouldSearchEmails
                ? searchEmails(p.id, {
                    searchTerm: cleanedInput,
                    keywordSearchTerms: cleanedKeywordSearchInputs,
                    createdBeforeDate,
                    createdAfterDate,
                    excludeHidden: true,
                  })
                : Promise.resolve({ emails: { results: [] } }),
              shouldSearchDesignFiles
                ? searchDesignFiles(p.id, {
                    searchTerm: cleanedInput,
                    keywordSearchTerms: cleanedKeywordSearchInputs,
                  })
                : Promise.resolve({ results: [] }),
              shouldSearchBidFiles
                ? // Additive to the other three sources, and this block has no catch of its own -
                  // a rejection here would take the whole search down with it.
                  searchBidFiles(p.id, {
                    searchTerm: cleanedInput,
                    keywordSearchTerms: cleanedKeywordSearchInputs,
                  }).catch(() => ({ results: [] }))
                : Promise.resolve({ results: [] }),
            ]);

            const groupedDocuments = _.groupBy(documentResults.results, 'documentTemplateId');

            const projectResult: IProjectSearchResult = {
              projectName: p.name,
              documents: groupedDocuments,
              emails: emailResults.emails.results,
              designFiles: designFilesResults.results,
              bidFiles: bidFilesResults.results,
            };

            setSearchResults((prev) => ({
              ...prev,
              [p.id]: projectResult,
            }));
          } finally {
            setProjectLoading((prev) => ({
              ...prev,
              [p.id]: false,
            }));
          }
        }),
      );

      if (keywordSearchInputs.length < 3 && keywordSearchInputs.every((k) => !!k.trim())) {
        setKeywordSearchInputs((prev) => [...prev, '']);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const getFilterCount = () => {
    let count = 0;

    if (documentTemplateIds.length > 0) count++;
    if (shouldSearchFileManager) count++;
    if (workflowStatuses.length > 0 || punchListStatuses.length > 0 || actionTakenValues.length > 0)
      count++;
    if (createdAfterDate || createdBeforeDate) count++;

    return count;
  };
  const filterCount = getFilterCount();

  const isSearchDisabled = () => {
    return isSearching || selectedProjects.length === 0 || searchInput.trim().length < 3;
  };

  if (pageIsLoading) {
    return (
      <main className={classes.content}>
        <NavAppbar />
        <FullscreenLoader />
      </main>
    );
  }

  if (!hasPermission) {
    return (
      <main className={classes.content}>
        <NavAppbar />
        <Grid
          container
          spacing={2}
          style={{
            display: 'flex',
            position: 'relative',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
            overflowY: 'hidden',
          }}
        >
          <Typography variant="h6">
            You do not have permission to access this feature. Please contact an administrator.
          </Typography>
        </Grid>
      </main>
    );
  }

  return (
    <main className={classes.content}>
      <NavAppbar />
      <PageTitle title="AI Word Finder" />
      <div className={classes.paper}>
        <div className={classes.searchForm}>
          <TextField
            className={classes.searchInput}
            size="small"
            placeholder="Find any word in any document..."
            variant="outlined"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={(e) => {
              if (isSearchDisabled()) return;
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          {!isSmall ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => handleSearch()}
              disabled={isSearchDisabled()}
            >
              Search
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSearch()}
              style={{ paddingLeft: 4, paddingRight: 4, minWidth: 36 }}
            >
              <SearchIcon />
            </Button>
          )}
          {!isSmall ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilters}
              style={{ marginLeft: 8, whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {filterCount ? `${filterCount} filters selected` : 'Filters'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenFilters}
              style={{ paddingLeft: 4, paddingRight: 4, minWidth: 36, marginLeft: 8 }}
            >
              <FilterListIcon />
            </Button>
          )}
          <DocumentSearchFilters
            open={isFiltersOpen}
            onClose={handleCloseFilters}
            onSubmit={handleSetFilters}
            defaultFilters={documentSearchFilters}
          />
        </div>

        {isSearching ? <EllipsisLoader text="Searching" style={{ marginLeft: 4 }} /> : null}

        {hasSearched ? (
          <div className={classes.resultsContainer}>
            {!isSearching ? (
              <>
                <Typography style={{ marginBottom: 16 }}>Refine your search further.</Typography>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  {keywordSearchInputs.map((input, index) => (
                    <TextField
                      key={index}
                      size="small"
                      label="Keyword"
                      variant="outlined"
                      value={input}
                      autoFocus
                      onChange={(event) =>
                        setKeywordSearchInputs((prev) =>
                          prev.map((v, i) => (i === index ? event.target.value : v)),
                        )
                      }
                      onKeyPress={(e) => {
                        if (isSearchDisabled()) return;
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      style={{
                        width: '33%',
                        marginRight: index === 2 ? 0 : 8,
                      }}
                    />
                  ))}
                  {keywordSearchInputs.length < 3 && (
                    <IconButton
                      onClick={() => setKeywordSearchInputs((prev) => [...prev, ''])}
                      style={{ padding: 0 }}
                    >
                      <AddCircleOutlined />
                    </IconButton>
                  )}
                </div>
              </>
            ) : null}

            {!defaultProjectId || (!!defaultProjectId && !isSearching) ? (
              <Typography
                variant="subtitle1"
                style={{ fontSize: 14, fontWeight: 500, color: '#464546', marginBottom: 8 }}
              >
                Found {allSearchResults.length} results for "{searchQuery}"
                {keywordSearchQueries.filter((k) => !!k).length > 0
                  ? `, ${keywordSearchQueries
                      .filter((k) => !!k)
                      .map((k) => `"${k}"`)
                      .join(', ')}`
                  : ''}
              </Typography>
            ) : null}
            {selectedProjects.map((p) => (
              <ProjectSearchResults
                key={p.id}
                results={
                  searchResults[p.id] ?? {
                    projectName: p.name,
                    documents: {},
                    emails: [],
                    designFiles: [],
                    bidFiles: [],
                  }
                }
                isLoading={Boolean(projectLoading[p.id])}
                searchQuery={searchQuery}
                keywordSearchQueries={keywordSearchQueries}
                createdBeforeDate={createdBeforeDate}
                createdAfterDate={createdAfterDate}
                autoExpand={selectedProjects.length === 1}
              />
            ))}
          </div>
        ) : null}
      </div>
    </main>
  );
}
